import React, { useState } from 'react';
import {
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
    Box,
    Divider,
    IconButton,
    useTheme as useMuiTheme,
    useMediaQuery,
    Tooltip,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle
} from '@mui/material';
import {
    Home,
    MenuBook,
    VideoLibrary,
    Menu as MenuIcon,
    Logout,
    School,
    Article,
    ShortText,
    SmartDisplay
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { authActions } from '../../_store';

const DRAWER_WIDTH = 280;
const COLLAPSED_WIDTH = 72;

const Sidebar = ({ open, collapsed: externalCollapsed, onClose, onToggle, darkMode, onDarkModeToggle }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const theme = useMuiTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    
    const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

    // Use external collapsed state for desktop, always false for mobile
    const collapsed = isMobile ? false : externalCollapsed;

    const handleNavigate = (path) => {
        navigate(path);
        if (isMobile) {
            onClose();
        }
    };

    const handleLogout = () => {
        setLogoutDialogOpen(true);
    };

    const handleCloseLogoutDialog = () => {
        setLogoutDialogOpen(false);
    };

    const handleConfirmLogout = () => {
        dispatch(authActions.logout());
        setLogoutDialogOpen(false);
    };

    const handleToggleCollapse = () => {
        if (onToggle) onToggle();
    };

    // Handle brand click - toggle sidebar
    const handleBrandClick = () => {
        if (!isMobile) {
            handleToggleCollapse();
        }
    };

    const isActive = (path) => {
        if (path === '/profile' && location.pathname === '/') return true;
        return location.pathname === path || location.pathname.startsWith(path + '/');
    };

    const menuItems = [
        { icon: <Home />, label: 'Home', path: '/profile' },
        { icon: <School />, label: 'Education', path: '/education' },
        { icon: <Article />, label: 'News', path: '/news' },
        { icon: <ShortText />, label: 'Short Reading', path: '/short-stories' },
        { icon: <MenuBook />, label: 'Longread', path: '/long-posts' },
        { icon: <VideoLibrary />, label: 'Video', path: '/video-posts' },
        { icon: <SmartDisplay />, label: 'Shorts', path: '/shorts' },
    ];

    // Brand Header - clickable to toggle sidebar
    const BrandHeader = () => (
        <Box sx={{ 
            p: 2, 
            display: 'flex', 
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${theme.palette.divider}`,
            minHeight: 64,
            backgroundColor: 'background.paper'
        }}>
            <Box 
                sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    cursor: !isMobile ? 'pointer' : 'default',
                    '&:hover': !isMobile ? {
                        opacity: 0.8
                    } : {}
                }}
                onClick={!isMobile ? handleBrandClick : undefined}
            >
                <img 
                    src="/icon.png" 
                    alt="TilBuzz Logo" 
                    style={{ 
                        width: '32px', 
                        height: '32px', 
                        marginRight: collapsed ? '0' : '12px',
                        borderRadius: '6px'
                    }} 
                />
                {!collapsed && (
                    <Typography
                        variant="h5"
                        sx={{
                            fontFamily: 'monospace',
                            fontWeight: 700,
                            letterSpacing: '.2rem',
                            color: 'primary.main',
                            fontSize: '1.3rem',
                            lineHeight: 1.2
                        }}
                    >
                        TilBuzz
                    </Typography>
                )}
            </Box>
            
            {/* Hide/Collapse button on the right - only show when sidebar is open */}
            {!isMobile && !collapsed && (
                <IconButton
                    onClick={handleToggleCollapse}
                    size="small"
                    sx={{
                        color: 'text.primary',
                        '&:hover': {
                            backgroundColor: 'action.hover'
                        }
                    }}
                >
                    <MenuIcon sx={{ 
                        transform: 'rotate(180deg)',
                        transition: 'transform 0.2s'
                    }} />
                </IconButton>
            )}
        </Box>
    );


    // Main menu items
    const MainMenuItems = () => (
        <List sx={{ overflow: 'hidden', flex: 1, py: 1 }}>
            {menuItems.map((item) => {
                const active = isActive(item.path);
                const isHome = item.label === 'Home';
                return (
                    <React.Fragment key={item.label}>
                        <ListItem disablePadding>
                            <ListItemButton
                                selected={active}
                                onClick={() => handleNavigate(item.path)}
                                sx={{
                                    borderRadius: 1,
                                    mx: 1,
                                    mb: 0.5,
                                    justifyContent: collapsed ? 'center' : 'flex-start',
                                    px: collapsed ? 1 : 2,
                                    '&.Mui-selected': {
                                        backgroundColor: 'primary.light',
                                        color: 'primary.contrastText',
                                        '&:hover': {
                                            backgroundColor: 'primary.main',
                                        },
                                        '& .MuiListItemIcon-root': {
                                            color: 'primary.contrastText',
                                        },
                                    },
                                }}
                            >
                                {collapsed ? (
                                    <Tooltip title={item.label} placement="right">
                                        {item.icon}
                                    </Tooltip>
                                ) : (
                                    <>
                                        <ListItemIcon
                                            sx={{
                                                color: active ? 'primary.contrastText' : 'inherit',
                                                minWidth: 40,
                                            }}
                                        >
                                            {item.icon}
                                        </ListItemIcon>
                                        <ListItemText primary={item.label} />
                                    </>
                                )}
                            </ListItemButton>
                        </ListItem>
                        {isHome && <Divider sx={{ mx: 2, my: 0.5 }} />}
                    </React.Fragment>
                );
            })}
        </List>
    );

    // Collapse toggle button moved to brand header

    const drawerContent = (
        <Box sx={{ 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column',
            backgroundColor: 'background.paper',
            position: 'relative'
        }}>
            <BrandHeader />
            
            <Divider />
            <MainMenuItems />

            <Box sx={{ flexGrow: 1 }} />

            <Divider />
            <Box sx={{ p: 2 }}>
                <ListItemButton
                    onClick={handleLogout}
                    sx={{
                        borderRadius: 1,
                        justifyContent: collapsed ? 'center' : 'flex-start',
                        px: collapsed ? 1.5 : 2,
                        py: 1.5
                    }}
                >
                    {collapsed ? (
                        <Tooltip title="Logout" placement="right">
                            <Logout />
                        </Tooltip>
                    ) : (
                        <>
                            <ListItemIcon sx={{ minWidth: 40 }}>
                                <Logout />
                            </ListItemIcon>
                            <ListItemText primary="Logout" />
                        </>
                    )}
                </ListItemButton>
            </Box>

            <Dialog
                open={logoutDialogOpen}
                onClose={handleCloseLogoutDialog}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">{"Confirm Logout"}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to log out?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseLogoutDialog}>Cancel</Button>
                    <Button onClick={handleConfirmLogout} color="primary" autoFocus>
                        Logout
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );

    const currentWidth = isMobile ? DRAWER_WIDTH : (collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH);

    return (
        <>
            {/* Mobile overlay */}
            {isMobile ? (
                <Drawer
                    anchor="left"
                    open={open}
                    onClose={onClose}
                    ModalProps={{
                        keepMounted: true,
                    }}
                    sx={{
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: DRAWER_WIDTH,
                            height: '100vh',
                        },
                    }}
                >
                    {drawerContent}
                </Drawer>
            ) : (
                /* Desktop persistent drawer */
                <Drawer
                    variant="persistent"
                    anchor="left"
                    open={open}
                    sx={{
                        width: currentWidth,
                        flexShrink: 0,
                        transition: theme.transitions.create('width', {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.enteringScreen,
                        }),
                        '& .MuiDrawer-paper': {
                            width: currentWidth,
                            boxSizing: 'border-box',
                            borderRight: `1px solid ${theme.palette.divider}`,
                            height: '100vh',
                            transition: theme.transitions.create('width', {
                                easing: theme.transitions.easing.sharp,
                                duration: theme.transitions.duration.enteringScreen,
                            }),
                        },
                    }}
                >
                    {drawerContent}
                </Drawer>
            )}
        </>
    );
};

export default Sidebar; 